import { toYear } from 'utils/date.util'
import {
  addIssueIfInvalid,
  doNotHasText,
  isFilled,
  validateField,
  validateFieldNumber,
  validateInfractions,
} from 'utils/validate.util'
import { RefinementCtx, z } from 'zod'
import { SystemMessages } from 'utils/enums/system-message.enum'

export const infractionTypeIdSchema = z.object({
  id: z.preprocess((field) => {
    return validateFieldNumber(field)
  }, z.string()),
  descricao: z.preprocess((field) => {
    return validateField(field)
  }, z.string()),
})

export type InfractionTypeIdType = z.infer<typeof infractionTypeIdSchema>

export const infractionTypeIdInit: InfractionTypeIdType = {
  id: '0',
  descricao: '',
}

export const infractionSchema = z
  .object({
    alinea: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    ano: z
      .preprocess((field) => {
        return validateField(field)
      }, z.string())
      .transform((field) => {
        return toYear(field)
      }),
    artigo: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    descricaoTipo: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    inciso: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    lei: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    paragrafo: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    tipo: infractionTypeIdSchema.nullish(),
  })
  .superRefine((fields, ctx) => {
    if (fields.tipo?.id === '3' && doNotHasText(fields.lei)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: SystemMessages.MESSAGE_002,
        path: ['lei'],
      })
    }

    if (fields.tipo?.id === '3' && !fields.ano) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: SystemMessages.MESSAGE_002,
        path: ['ano'],
      })
    }
  })

export type InfractionType = z.infer<typeof infractionSchema>

export const infractionInit: InfractionType = {
  alinea: '',
  ano: 0,
  artigo: '',
  descricaoTipo: '',
  inciso: '',
  lei: '',
  paragrafo: '',
  tipo: infractionTypeIdInit,
}

export const infractionArray = z
  .array(infractionSchema)
  .superRefine((infractions, ctx) => {
    infractions.forEach((infraction, index) => {
      const otherFieldsFilled = isFilled({
        ...infraction,
        artigo: undefined,
        tipo: { id: undefined },
      })

      const artigoIsEmpty = !isFilled(infraction.artigo)
      const tipoIdIsEmpty = !isFilled(infraction.tipo?.id)

      if (artigoIsEmpty) {
        // Validação do campo artigo
        addIssueIfInvalid(
          ctx,
          infractions.length > 1,
          index,
          ['artigo'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas artigo vazio
        addIssueIfInvalid(
          ctx,
          otherFieldsFilled && artigoIsEmpty,
          index,
          ['artigo'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas artigo vazio
        addIssueIfInvalid(ctx, !tipoIdIsEmpty, index, ['artigo'], SystemMessages.MESSAGE_002)
      }

      if (tipoIdIsEmpty) {
        // Validação do campo tipoReferencia.id
        addIssueIfInvalid(
          ctx,
          infractions.length > 1,
          index,
          ['tipo', 'id'],
          SystemMessages.MESSAGE_002,
        )

        // Validação para outros campos preenchidos, mas tipoReferencia.id vazio
        addIssueIfInvalid(ctx, otherFieldsFilled, index, ['tipo', 'id'], SystemMessages.MESSAGE_002)

        // Validação para outros campos preenchidos, mas tipoReferencia.id vazio
        addIssueIfInvalid(ctx, !artigoIsEmpty, index, ['tipo', 'id'], SystemMessages.MESSAGE_002)
      }
    })
  })
  .transform((fields) => {
    return validateInfractions(fields)
  })
  .nullish()
